import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Microscope, Users, Activity, Compass, GraduationCap, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { listInterviews, subscribeNewsletter } from "../lib/api";
import InterviewCard from "../components/InterviewCard";

const HERO_IMG = "https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?w=1400&auto=format&fit=crop";

const MARQUEE = [
  "Launching soon",
  "Healthcare education for the next generation",
  "Conversations with physicians, researchers & founders",
  "Making medicine accessible",
  "Real stories from real professionals",
  "Built for curious students",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    listInterviews({ featured: true, limit: 6 })
      .then((data) => {
        if (data.length === 0) return listInterviews({ limit: 6 }).then(setFeatured);
        setFeatured(data);
      })
      .catch(() => {});
  }, []);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list. Welcome.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="home-page" className="bg-white">
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[86vh]">
          <div className="flex items-center px-6 lg:pl-16 lg:pr-12 py-16 lg:py-24">
            <div className="max-w-xl animate-fade-up">
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-medblue" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">Healthcare Education Archive</span>
              </div>
              <h1 className="font-serif text-[44px] md:text-[56px] lg:text-[68px] leading-[1.03] tracking-tight text-navy">
                Learn From The People Shaping The Future Of Medicine.
              </h1>
              <p className="mt-8 text-[17px] text-navy/65 leading-[1.7]">
                Tomorrow&rsquo;s Doctors brings students closer to physicians, researchers, and healthcare innovators through
                conversations that reveal the reality of medicine, science, and the future of healthcare.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link to="/interviews" className="btn-primary" data-testid="cta-explore">
                  Explore Interviews <ArrowRight size={14} className="ml-2" />
                </Link>
                <Link to="/join" className="btn-secondary" data-testid="cta-interviewee">
                  Become An Interviewee
                </Link>
              </div>
            </div>
          </div>
          <div className="relative bg-parchment overflow-hidden">
            <img
              src={HERO_IMG}
              alt="Physician portrait"
              className="w-full h-full object-cover grayscale contrast-105"
              style={{ minHeight: "50vh" }}
            />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-rule bg-white overflow-hidden py-5" data-testid="marquee">
        <div className="marquee flex items-center gap-14 whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((t, i) => (
            <div key={i} className="flex items-center gap-14 shrink-0">
              <span className="text-[11px] uppercase tracking-[0.28em] text-navy/70">{t}</span>
              <span className="w-1 h-1 rounded-full bg-medblue" />
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="container-wide py-24 lg:py-32" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-medblue" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">Featured Conversations</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight">The archive, hand-selected.</h2>
          </div>
          <Link to="/interviews" className="text-[11px] uppercase tracking-[0.24em] text-medblue hover:text-navy inline-flex items-center gap-2 self-start md:self-end">
            View all interviews <ArrowRight size={13} />
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="border border-dashed border-navy/25 py-24 lg:py-28 text-center" data-testid="featured-empty">
            <div className="font-serif text-3xl md:text-4xl text-navy/35 italic">Interviews coming soon.</div>
            <p className="mt-5 text-navy/60 max-w-md mx-auto">
              We&rsquo;re currently scheduling our first conversations with healthcare professionals. Check back soon.
            </p>
            <Link to="/join" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-medblue hover:text-navy">
              Want to be featured? Get in touch <ArrowRight size={13} />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.slice(0, 6).map((iv, i) => <InterviewCard key={iv.id} interview={iv} index={i} />)}
          </div>
        )}
      </section>

      {/* WHY WE EXIST */}
      <section className="bg-parchment py-24 lg:py-32" data-testid="mission-section">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-medgreen" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-medgreen">Why We Exist</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[42px] text-navy leading-[1.15] tracking-tight">
              The path to medicine shouldn&rsquo;t be a mystery reserved for the well-connected.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[17px] text-navy/70 leading-[1.75]">
            <p>
              Tomorrow&rsquo;s Doctors was built on a simple conviction: every student curious about medicine deserves to
              hear the truth from the people living it. Too many talented minds abandon the path not for lack of ability,
              but for lack of access — to mentors, to realistic information, to a window into the daily reality of a
              healthcare career.
            </p>
            <p>
              We exist to open that window. By archiving candid conversations with physicians, researchers, founders, and
              students, we replace rumor with reality and aspiration with a roadmap.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
              {[
                { icon: FlaskConical, title: "Real Stories", desc: "Unfiltered conversations with people actively shaping medicine today." },
                { icon: Compass, title: "Career Clarity", desc: "Understand the reality of the path before committing years to it." },
                { icon: GraduationCap, title: "Actionable Guidance", desc: "Concrete advice on research, shadowing, and the med school journey." },
              ].map((b, i) => (
                <div key={i} className="border-l-2 border-medblue pl-5">
                  <b.icon size={22} strokeWidth={1.5} className="text-medblue" />
                  <div className="mt-3 font-serif text-xl text-navy">{b.title}</div>
                  <p className="mt-2 text-sm text-navy/65 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STUDENT IMPACT */}
      <section className="container-wide py-24 lg:py-32" data-testid="stats-section">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-8 bg-medblue" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">Student Impact</span>
            <span className="h-px w-8 bg-medblue" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-navy tracking-tight">What we&rsquo;re building toward.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-rule">
          {[
            { icon: Stethoscope, title: "Physicians & Professionals", caption: "Conversations being scheduled" },
            { icon: Microscope, title: "Medical Specialties", caption: "Across all areas of healthcare" },
            { icon: Users, title: "Students", caption: "Archive launching soon" },
            { icon: Activity, title: "Hours of Mentorship", caption: "Building the archive now" },
          ].map((s, i) => (
            <div key={i} className="px-8 py-12 text-center border-r border-rule last:border-r-0 md:[&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r" data-testid={`stat-${i}`}>
              <s.icon size={28} strokeWidth={1.5} className="text-medblue mx-auto" />
              <div className="mt-5 font-serif text-2xl text-navy leading-tight">{s.title}</div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-medgreen">{s.caption}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NEWSLETTER (dark) */}
      <section className="bg-navy text-white" data-testid="newsletter-section">
        <div className="container-wide py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-medblue" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-medblue">The Impact Pulse</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Join the future of medicine.</h2>
            <p className="mt-5 text-white/60 max-w-md leading-relaxed">
              Get new interviews, resources, and mentorship opportunities delivered to your inbox.
            </p>
          </div>
          <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-white/5 border border-white/15 focus:border-medblue outline-none px-5 py-4 text-white placeholder-white/40 text-sm"
              data-testid="newsletter-input"
            />
            <button type="submit" disabled={subscribing} className="bg-medblue text-white hover:bg-white hover:text-navy transition-colors px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center justify-center gap-2" data-testid="newsletter-submit">
              {subscribing ? "…" : <>Subscribe <ArrowRight size={14} /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
